import React from 'react';

import { Box, FormControlLabel, FormGroup, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { CollectionInput, Option } from '@/components/DynamicForm/types/DynamicFormTypes';
import ImageWrapper from '@/components/ImageWrapper';
import { applyLayoutStyles } from '@/components/DynamicForm/layout.styles.handler';

import {
	getFormControlLabelStyles,
	getFormGroupStyles,
	getFormLabelBoxWrapper,
	getSelectedOptionIconStyles,
	imageStyles,
} from './SingleAndMultiSelect.styles';

interface IQuestions extends CollectionInput {
	eyebrow?: string;
	description?: string;
}

export const SingleAndMultiSelect = ({
	errMsg,
	name,
	label,
	eyebrow,
	value = [],
	onChange,
	onBlur,
	description,
	options = [],
	showError,
	layout,
	optionsLayout = {},
}: IQuestions) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const selectedValues = Array.isArray(value) ? value : [value];

	const onChangeHandler = (event: React.MouseEvent<HTMLLabelElement, MouseEvent>, option: Option) => {
		if (onChange) {
			onChange(event, { value: option.value, fieldName: name });
		}
	};

	return (
		<Grid container direction="column" justifyContent="center" alignItems="center" gap={2}>
			<Grid item>
				<Typography variant="header2" color="text.primary" textAlign="center">
					{label}
				</Typography>
			</Grid>

			{description ? (
				<Grid item textAlign="center">
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>{description}</Typography>
				</Grid>
			) : null}

			{eyebrow ? (
				<Grid item>
					<Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>{eyebrow}</Typography>
				</Grid>
			) : null}

			{showError ? (
				<Grid item>
					<Typography variant="notification" color="error.dark" data-test-id="question_field_error_message">
						{errMsg}
					</Typography>
				</Grid>
			) : null}

			<Grid item width="100%">
				<FormGroup sx={applyLayoutStyles(getFormGroupStyles, layout)}>
					{options.map((option) => {
						const isOptionSelected = selectedValues.includes(option.value);
						const selectedOptionFontVariant = isSmallScreen ? 'bodyMediumSemiBold' : 'bodyLargeSemiBold';
						const defaultOptionFontVariant = isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook';
						return (
							<FormControlLabel
								key={option.value}
								sx={getFormControlLabelStyles}
								labelPlacement="end"
								onBlur={onBlur}
								onClick={(event) => onChangeHandler(event, option)}
								label=""
								control={
									<Box
										sx={applyLayoutStyles(
											getFormLabelBoxWrapper(theme, isOptionSelected, !!option?.imageUrl),
											{
												...optionsLayout,
												...option.layout,
											}
										)}
										component="div"
										data-test-id={`option_${option.value}`}
									>
										{option.imageUrl && <ImageWrapper src={option.imageUrl} sx={imageStyles} />}
										<Typography
											color="text.interactive"
											variant={
												isOptionSelected ? selectedOptionFontVariant : defaultOptionFontVariant
											}
											textAlign="center"
										>
											{option.label}
										</Typography>
										{isOptionSelected ? (
											<CheckCircleIcon color="secondary" sx={getSelectedOptionIconStyles} />
										) : null}
									</Box>
								}
							/>
						);
					})}
				</FormGroup>
			</Grid>
		</Grid>
	);
};
